/**
 * Actual vs Budget P&L (422477). Actuals are posted journals of the chosen periods; budget is the
 * year's approved budget. Pick no cost centre for the whole company, one, or several (consolidated).
 * Variance is favourable when positive: income above budget, expenditure below it.
 */
import { useEffect, useState } from 'react';
import { Scale, AlertTriangle, Info } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { financialYearApi } from '@/api/financialYear.api';
import { budgetApi, type Budget, type BudgetVsActual } from '@/api/budget.api';

const errorText = (e: unknown) =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'The report could not be run.';
const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const control = 'px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white';
const label = 'block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-1';

export const BudgetVsActualPage = () => {
  const [years, setYears]       = useState<{ yearCode: string; isCurrent: boolean }[]>([]);
  const [year, setYear]         = useState('');
  const [setup, setSetup]       = useState<Budget | null>(null);   // periods, accounts, cost centres for the filters
  const [fromPeriod, setFrom]   = useState<number | ''>('');
  const [toPeriod, setTo]       = useState<number | ''>('');
  const [costCenterIds, setCcs] = useState<string[]>([]);
  const [accountId, setAccount] = useState('');
  const [report, setReport]     = useState<BudgetVsActual | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);

  useEffect(() => {
    financialYearApi.getList({ pageSize: 50 }).then(r => {
      const list = r.data?.data?.items ?? [];
      setYears(list);
      setYear((list.find(y => y.isCurrent) ?? list[0])?.yearCode ?? '');
    }).catch(() => setYears([]));
  }, []);

  useEffect(() => {
    if (!year) return;
    setReport(null); setFrom(''); setTo('');
    budgetApi.get(year).then(setSetup).catch(e => { setSetup(null); setError(errorText(e)); });
  }, [year]);

  const run = async () => {
    setLoading(true); setError(null);
    try {
      setReport(await budgetApi.vsActual(year, {
        fromPeriod: fromPeriod || undefined, toPeriod: toPeriod || undefined,
        costCenterIds: costCenterIds.length ? costCenterIds : undefined, accountId: accountId || undefined,
      }));
    } catch (e) { setError(errorText(e)); }
    finally { setLoading(false); }
  };

  const tone = (v: number) => (v > 0 ? 'text-emerald-700 dark:text-emerald-400' : v < 0 ? 'text-red-600 dark:text-red-400' : '');

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <Scale size={20} className="text-signal" /> Actual vs Budget P&amp;L
        </h1>
        <div className="flex items-end gap-3 flex-wrap mt-3">
          <div><span className={label}>Year</span>
            <select className={control} value={year} onChange={e => setYear(e.target.value)}>
              {years.map(y => <option key={y.yearCode} value={y.yearCode}>{y.yearCode}</option>)}
            </select>
          </div>
          {(['From', 'To'] as const).map(which => (
            <div key={which}><span className={label}>{which} period</span>
              <select className={control} value={which === 'From' ? fromPeriod : toPeriod}
                onChange={e => (which === 'From' ? setFrom : setTo)(e.target.value ? Number(e.target.value) : '')}>
                <option value="">{which === 'From' ? 'First' : 'Last'}</option>
                {setup?.periods.map(p => <option key={p.periodNumber} value={p.periodNumber}>{p.periodName}</option>)}
              </select>
            </div>
          ))}
          <div><span className={label}>Cost centres <span className="normal-case font-normal">(none = all; Ctrl-click for several)</span></span>
            <select multiple className={`${control} h-[4.5rem] min-w-[12rem]`} value={costCenterIds}
              onChange={e => setCcs(Array.from(e.target.selectedOptions, o => o.value))}>
              {setup?.costCenters.map(c => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
            </select>
          </div>
          <div><span className={label}>GL account</span>
            <select className={`${control} max-w-[16rem]`} value={accountId} onChange={e => setAccount(e.target.value)}>
              <option value="">All</option>
              {setup?.accounts.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
            </select>
          </div>
          <Button size="sm" loading={loading} disabled={!year} onClick={() => void run()}>Run</Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-4">
        {error && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}
        {report?.notice && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-800 dark:text-amber-300">
            <Info size={16} className="mt-0.5 shrink-0" /> {report.notice}
          </div>
        )}
        {loading && <div className="flex items-center justify-center h-40"><Spinner size="lg" /></div>}
        {report && !loading && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto">
            <div className="px-4 py-2.5 text-xs text-slate-500 border-b border-slate-100 dark:border-slate-700">
              {report.financialYearCode} · {report.fromPeriod} – {report.toPeriod}
              {costCenterIds.length > 1 ? ` · consolidated, ${costCenterIds.length} cost centres` : ''}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60">
                <tr className="text-xs text-slate-500 dark:text-gray-400 uppercase">
                  <th className="px-4 py-2 text-left">Account</th>
                  <th className="px-4 py-2 text-right">Budget</th>
                  <th className="px-4 py-2 text-right">Actual</th>
                  <th className="px-4 py-2 text-right">Variance</th>
                  <th className="px-4 py-2 text-right">Variance %</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r, i) => {
                  const strong = r.rowType !== 'ACCOUNT';
                  const sectionStart = r.rowType === 'ACCOUNT' && report.rows[i - 1]?.section !== r.section;
                  return [
                    sectionStart && (
                      <tr key={`h-${r.section}`}><td colSpan={5} className="px-4 pt-3 pb-1 text-xs font-semibold uppercase text-slate-500">{r.section}</td></tr>
                    ),
                    <tr key={i} className={`${strong ? 'font-semibold bg-slate-50/60 dark:bg-slate-800/40' : ''} ${r.rowType === 'NET' ? 'border-t-2 border-slate-300 dark:border-slate-600' : 'border-t border-slate-100 dark:border-slate-800'}`}>
                      <td className="px-4 py-2">{r.code ? <span className="font-mono text-xs text-slate-500 mr-2">{r.code}</span> : null}{r.name}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{money(r.budget)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{money(r.actual)}</td>
                      <td className={`px-4 py-2 text-right tabular-nums ${tone(r.variance)}`}>{money(r.variance)}</td>
                      <td className={`px-4 py-2 text-right tabular-nums ${tone(r.variance)}`}>{r.variancePct == null ? '—' : `${r.variancePct.toFixed(1)}%`}</td>
                    </tr>,
                  ];
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
